import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AtSign, Mail, MoreVertical, Phone, Plus } from "lucide-react";
import {
  formatIdentifier,
  isNameIdentifier,
  type IdentifierType,
  type UserDetailDto,
  type UserIdentifierDto,
} from "@platform/core";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Field,
  IconButton,
  Input,
  Menu,
  NativeSelect,
  DataGrid,
  useToast,
  type GridColDef,
} from "@platform/ui";
import { useApi, useConsole } from "../../context";
import { useT } from "../../i18n";
import { errorText } from "../../lib/errors";
import { formatDate } from "../../lib/format";
import { ConfirmDialog } from "../../layout/ConfirmDialog";
import { useGridText } from "../../lib/grid";
import { ErrorBanner } from "../../layout/Page";

type Confirm = { kind: "remove" | "primary"; item: UserIdentifierDto };

export function IdentifiersTab({ user }: { user: UserDetailDto }) {
  const { t, locale } = useT();
  const api = useApi();
  const qc = useQueryClient();
  const { can, config } = useConsole();
  const toast = useToast();
  const text = useGridText();
  const writable = can("users:write");
  const key = ["user", user.id, "identifiers"];
  const list = useQuery({ queryKey: key, queryFn: () => api.users.identifiers.list(user.id) });
  const [type, setType] = useState<IdentifierType>("email");
  const [value, setValue] = useState("");
  const [verified, setVerified] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ["user", user.id] });
  };
  const add = useMutation({
    mutationFn: () => api.users.identifiers.add(user.id, { type, value: value.trim(), verified }),
    onSuccess: () => {
      setValue("");
      setVerified(false);
      setAddError(null);
      toast({ title: t("ident.addedToast"), tone: "success" });
      refresh();
    },
    onError: (e) => setAddError(errorText(e)),
  });
  const act = useMutation({
    mutationFn: async (a: { kind: "remove" | "primary" | "verify"; id: string }) => {
      if (a.kind === "remove") await api.users.identifiers.remove(user.id, a.id);
      else if (a.kind === "primary") await api.users.identifiers.makePrimary(user.id, a.id);
      else await api.users.identifiers.markVerified(user.id, a.id);
      return a.kind;
    },
    onSuccess: (kind) => {
      setConfirm(null);
      toast({
        title: t(
          kind === "remove"
            ? "ident.removedToast"
            : kind === "primary"
              ? "ident.primaryToast"
              : "ident.verifiedToast",
        ),
        tone: "success",
      });
      refresh();
    },
  });

  if (list.isError) return <ErrorBanner error={list.error} onRetry={() => void list.refetch()} />;
  const items = list.data?.items ?? [];
  const columns: GridColDef<UserIdentifierDto>[] = [
    {
      field: "value",
      headerName: t("ident.value"),
      minWidth: 260,
      hideable: false,
      valueFormatter: (_v, i) => formatIdentifier(i.type, i.value),
      renderCell: ({ row: i, formatted }) => {
        const Icon = i.type === "email" ? Mail : i.type === "phone" ? Phone : AtSign;
        return (
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-subtle text-ink-2 ring-1 ring-line ring-inset"
            >
              <Icon className="size-4" />
            </span>
            <div className="min-w-0">
              <p className="truncate font-medium">{formatted}</p>
              <p className="text-xs text-ink-2">{t(`ident.type.${i.type}`)}</p>
            </div>
          </div>
        );
      },
    },
    {
      field: "type",
      headerName: t("ident.type"),
      type: "singleSelect",
      width: 140,
      hiddenByDefault: true,
      valueOptions: [
        { value: "email", label: t("ident.type.email") },
        { value: "phone", label: t("ident.type.phone") },
        { value: "user_code", label: t("ident.type.user_code") },
        { value: "username", label: t("ident.type.username") },
      ],
    },
    {
      field: "verified",
      headerName: t("ident.status"),
      type: "boolean",
      width: 190,
      valueFormatter: (v, i) =>
        `${i.primary ? `${t("ident.primary")} · ` : ""}${v ? t("ident.verified") : t("ident.unverified")}`,
      renderCell: ({ row: i }) => (
        <div className="flex flex-wrap gap-1">
          {i.primary && <Badge tone="info">{t("ident.primary")}</Badge>}
          <Badge dot tone={i.verified ? "success" : "warning"}>
            {i.verified ? t("ident.verified") : t("ident.unverified")}
          </Badge>
        </div>
      ),
    },
    {
      field: "createdAt",
      headerName: t("ident.added"),
      type: "dateTime",
      width: 150,
      valueFormatter: (v) => formatDate(String(v), locale, config.timeZone),
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      width: 64,
      align: "right",
      hideable: false,
      resizable: false,
      disableExport: true,
      renderCell: ({ row: i }) =>
        writable && i.id !== "primary-email" ? (
          <Menu.Root>
            <Menu.Trigger asChild>
              <IconButton label={t("ident.actions", { value: i.value })} className="size-8">
                <MoreVertical className="size-4" aria-hidden />
              </IconButton>
            </Menu.Trigger>
            <Menu.Content>
              {!i.verified && (
                <Menu.Item onSelect={() => act.mutate({ kind: "verify", id: i.id })}>
                  {t("ident.verifyNow")}
                </Menu.Item>
              )}
              {i.verified && !i.primary && !isNameIdentifier(i.type) && (
                <Menu.Item
                  onSelect={() =>
                    i.type === "email"
                      ? setConfirm({ kind: "primary", item: i })
                      : act.mutate({ kind: "primary", id: i.id })
                  }
                >
                  {t("ident.makePrimary")}
                </Menu.Item>
              )}
              <Menu.Separator />
              <Menu.Item destructive onSelect={() => setConfirm({ kind: "remove", item: i })}>
                {t("ident.remove")}
              </Menu.Item>
            </Menu.Content>
          </Menu.Root>
        ) : null,
    },
  ];

  return (
    <div>
      <p className="px-6 pt-4 text-sm text-ink-2">{t("ident.hint")}</p>
      <DataGrid
        id="user-identifiers"
        label={t("user.tab.identifiers")}
        localeText={text}
        rows={items}
        columns={columns}
        loading={list.isFetching}
        pagination={false}
        disableQuickFilter
        getRowAttributes={(i) => ({ "data-testid": "identifier-row", "data-value": i.value })}
      />

      {writable && (
        <form
          className="border-t border-line px-6 py-5"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (value.trim()) add.mutate();
          }}
        >
          <h3 className="mb-3 text-sm font-semibold">{t("ident.addTitle")}</h3>
          {addError && (
            <Alert tone="error" className="mb-3">
              {addError}
            </Alert>
          )}
          {list.data && !list.data.smsAvailable && type === "phone" && !verified && (
            <Alert tone="warning" className="mb-3">
              {t("signin.smsMissing")}
            </Alert>
          )}
          <div className="grid gap-x-3 sm:grid-cols-[160px_1fr_auto]">
            <Field label={t("ident.type")}>
              {(p) => (
                <NativeSelect {...p} value={type} onChange={(e) => setType(e.target.value as IdentifierType)}>
                  <option value="email">{t("ident.type.email")}</option>
                  <option value="phone">{t("ident.type.phone")}</option>
                  <option value="user_code">{t("ident.type.user_code")}</option>
                  <option value="username">{t("ident.type.username")}</option>
                </NativeSelect>
              )}
            </Field>
            <Field
              label={t("ident.value")}
              hint={
                type === "phone"
                  ? t("ident.valuePhoneHint")
                  : isNameIdentifier(type)
                    ? t("ident.valueNameHint")
                    : undefined
              }
            >
              {(p) => (
                <Input
                  {...p}
                  data-testid="identifier-value"
                  type={type === "email" ? "email" : type === "phone" ? "tel" : "text"}
                  value={value}
                  placeholder={
                    type === "email" ? "name@example.com" : type === "phone" ? "081 234 5678" : "somchai.j"
                  }
                  onChange={(e) => setValue(e.target.value)}
                />
              )}
            </Field>
            <div className="mb-4 flex items-end">
              <Button
                type="submit"
                loading={add.isPending}
                disabled={!value.trim()}
                data-testid="identifier-add"
              >
                <Plus className="size-4" aria-hidden />
                {t("ident.add")}
              </Button>
            </div>
          </div>
          <Checkbox
            label={t("ident.markVerifiedOnAdd")}
            checked={verified}
            onChange={(e) => setVerified(e.target.checked)}
          />
          <p className="mt-1 ml-6 text-xs text-ink-2">{t("ident.markVerifiedHint")}</p>
        </form>
      )}

      <ConfirmDialog
        open={confirm !== null}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.kind === "primary" ? t("ident.primaryTitle") : t("ident.removeTitle")}
        confirmLabel={confirm?.kind === "primary" ? t("ident.makePrimary") : t("ident.remove")}
        destructive={confirm?.kind === "remove"}
        loading={act.isPending}
        onConfirm={() => confirm && act.mutate({ kind: confirm.kind, id: confirm.item.id })}
      >
        <p className="text-sm">
          {confirm?.kind === "primary"
            ? t("ident.primaryBody", { value: confirm.item.value })
            : t("ident.removeBody", { value: confirm?.item.value ?? "" })}
        </p>
      </ConfirmDialog>
    </div>
  );
}
